import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Spin, Tabs, Typography } from "antd";
import { LinkOutlined } from "@ant-design/icons";
import HeaderSection from "./components/HeaderSection";
import DashboardTemplate from "./DashboardTemplate";
import { getPublicDashboard } from "./services/dashboard.service";
import type { DashboardAcceptedApi, DashboardCardWithData } from "../../types/types";
import { HeaderOptions, DEFAULT_HEADER } from "../../types/dashboard";
import "./DashboardLayout.css";

const { Title, Text } = Typography;

const toHeaderOptions = (dashboard: DashboardAcceptedApi): HeaderOptions => {
  const h = dashboard.header_config || {};
  return {
    ...DEFAULT_HEADER,
    fontSize: Number(h.font_size ?? DEFAULT_HEADER.fontSize),
    fontFamily: h.font_type ?? DEFAULT_HEADER.fontFamily,
    titleColor: h.font_color ?? DEFAULT_HEADER.titleColor,
    subtitleColor: h.font_color ?? DEFAULT_HEADER.subtitleColor,
    paddingLeft: Number(String(h.padding_left ?? DEFAULT_HEADER.paddingLeft).replace("px", "")),
    paddingRight: Number(String(h.padding_right ?? DEFAULT_HEADER.paddingRight).replace("px", "")),
    backgroundColor: h.background_color ?? DEFAULT_HEADER.backgroundColor,
    borderColor: h.border_color ?? DEFAULT_HEADER.borderColor,
    borderWidth: Number(h.border_stroke ?? DEFAULT_HEADER.borderWidth),
    radius: Number(h.border_radius ?? DEFAULT_HEADER.radius),
    logoUrl: h.image_url ?? "",
    logoAlign: String(h.image_alignment ?? "Right").toLowerCase(),
    headerImageAlign: String(h.image_alignment ?? "Right").toLowerCase(),
  };
};

const PublicDashboardView: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<DashboardAcceptedApi | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeKey, setActiveKey] = useState("");

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        setLoading(true);
        const data = await getPublicDashboard(token);
        setDashboard(data);
        if (data.tabs?.length) setActiveKey(data.tabs[0].id);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 403) {
          setError("This link has been disabled by the owner.");
        } else if (status === 404) {
          setError("Dashboard not found.");
        } else {
          setError("Failed to load dashboard.");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 12,
          color: "#888",
        }}
      >
        <LinkOutlined style={{ fontSize: 40, color: "#ccc" }} />
        <Title level={4} style={{ margin: 0, color: "#555" }}>
          {error || "Dashboard not found."}
        </Title>
        <Text style={{ color: "#aaa", fontSize: 13 }}>
          The link may have expired or been disabled.
        </Text>
      </div>
    );
  }

  const headerOptions = toHeaderOptions(dashboard);
  const tabItems = (dashboard.tabs || []).map((tab) => ({
    key: tab.id,
    label: tab.name,
    closable: false,
    children: (
      <DashboardTemplate
        key={tab.id}
        tab={tab}
        dashboard={dashboard}
        cards={(tab.cards || []) as DashboardCardWithData[]}
        onOpenAdd={() => {}}
        onDeleteCard={() => {}}
        onApplyDrillDown={() => {}}
        onClearDrillDown={() => {}}
        drillDownFilters={{}}
        columns={[]}
        onUpdateCard={async () => {}}
        readOnly={true}
      />
    ),
  }));

  return (
    <div className="public-dashboard-wrapper">
      <div className="public-dashboard-container">
        <HeaderSection
          title={dashboard.name || "Dashboard"}
          subtitle=""
          editingTitle={false}
          headerOptions={headerOptions}
          setTitle={() => {}}
          setEditingTitle={() => {}}
          readOnly={true}
        />

        <Tabs
          type="card"
          className="dashboard-preview-tabs"
          tabBarStyle={{ margin: 0, padding: "0 16px 0 24px" }}
          items={tabItems}
          activeKey={activeKey || tabItems?.[0]?.key}
          onChange={setActiveKey}
        />
      </div>

      <div style={{ textAlign: "center", marginTop: 24, color: "#bbb", fontSize: 12 }}>
        Powered by <strong style={{ color: "#A072EB" }}>KAPinsights</strong>
      </div>
    </div>
  );
};

export default PublicDashboardView;
